<template>
  <div v-if="article && article.reactions">
    <!-- reaction and count -->
    <div class="u-center hover:cursor-pointer" @click.stop="handleClick">
      <OImage v-if="maxReaction" loading="lazy" :src="maxReaction.icons.svg || maxReaction.icons.png" class="h-6 w-6 mr-1" alt="" />
      <p v-if="maxReaction" class="font-medium text-primary-300 text-sm text-center leading-16">
        {{ maxReaction.count }}
      </p>
    </div>
    <!-- desktop -->
    <div
      v-if="!isMobile && isMenuOpen"
      ref="desktopReactionsRef"
      class="absolute bg-white z-3 p-2 right-[-200px] bottom-[-45px] rounded-md shadow"
    >
      <div class="u-center space-x-2">
        <div v-for="(reaction, i) in reactions" :id="reaction.id" :key="i" class="u-center cursor-pointer">
          <div
            class="relative rounded-full u-center w-10 h-10 ring-1 ring-[#E2E5ED]"
            :class="reaction.isActive && 'bg-[#41E89A] ring-[#41E89A]'"
            @click.stop="handleReaction(reaction)"
          >
            <img :src="reaction.icons.svg" class="w-[28.45px] h-[28.45px]" loading="lazy" />
          </div>
        </div>
      </div>
      <div v-if="showWarning" class="text-red-500 text-center text-sm -ml-3 mt-4">{{ $t('news-page.emoji-reaction-limit') }}</div>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, onMounted, ref, useContext, inject } from '@nuxtjs/composition-api';
import { sendReactionCommentLayer } from '@/modules/tracking/scripts/dataLayer';
import { useDialog } from '~/composables/useModal';
import { useDevice } from '~/composables/useDevice';
import { useAuth } from '~/modules/auth/state/useAuth';
import { useOutsideClick } from '~/composables/useOutsideClick';
import { localStore } from '~/modules/common/utils/localStorageUtils';
import { OIcon, OButton, OImage } from '~/modules/common/components/ui';
import { eventBus } from '~/modules/common/utils/eventBus';

export default defineComponent({
  components: { OIcon, OButton, OImage },
  setup() {
    const { $service } = useContext();
    const { openErrorDialog } = useDialog();
    const { isMobile } = useDevice();
    const { auth } = useAuth();

    const article = inject('article');
    const name = inject('dataLayerName')?.value;
    const articleId = article.legacyId;
    const userId = auth.value.isLoggedIn ? auth.value.user._id : 'anonym';
    const storageUserKey = `u${userId}`;
    const userReactions = ref({});
    const isReactionsDisabled = ref(false);
    const isMenuOpen = ref(false);
    const showWarning = ref(false);
    const desktopReactionsRef = ref();
    const reactions = ref(article.reactions);

    const updateReactions = () => {
      const userReactions = getCurrentUserReactions();
      reactions.value?.map((reaction) => {
        if (process.client) reaction.isActive = userReactions.includes(reaction.id);
      });
    };

    const maxReaction = computed(() =>
      article.reactions?.reduce((r1, r2) => {
        return r1 && r1.count >= r2.count ? r1 : r2;
      }, null)
    );

    useOutsideClick(desktopReactionsRef, () => {
      if (isMenuOpen.value) isMenuOpen.value = false;
    });

    const handleClick = () => {
      isMenuOpen.value = true;
      eventBus.emit('isArticleReactionsModalOpen', article);
    };

    const closeMenu = () => {
      isMenuOpen.value = false;
    };

    const getCurrentUserReactions = () => {
      const result = localStore.getItem('o-rcts')?.[storageUserKey]?.[articleId];
      return result || [];
    };

    const addReactionStorage = (id) => {
      const newReactions = [...getCurrentUserReactions(), id];
      userReactions.value[storageUserKey][articleId] = newReactions;
      localStore.setItem('o-rcts', userReactions.value);
    };

    const removeReactionStorage = (id) => {
      const newReactions = getCurrentUserReactions().filter((itemId) => itemId !== id);
      userReactions.value[storageUserKey][articleId] = newReactions;
      localStore.setItem('o-rcts', userReactions.value);
    };

    const handleReaction = async (reaction) => {
      isReactionsDisabled.value = true;
      try {
        if (getCurrentUserReactions().includes(reaction.id)) {
          if (getCurrentUserReactions().length <= 3) {
            showWarning.value = false;
          }
          await $service.article.removeReaction(article._id, reaction.id);
          reaction.count -= 1;
          removeReactionStorage(reaction.id);
        } else {
          if (getCurrentUserReactions().length === 3) {
            showWarning.value = true;
            return;
          }
          await $service.article.addReaction(article._id, reaction.id);
          reaction.count += 1;
          addReactionStorage(reaction.id);
          sendReactionCommentLayer('reaction', { article }, reaction, name, 'Keşfet');
        }
        updateReactions();
      } catch (error) {
        openErrorDialog({ error });
      } finally {
        isReactionsDisabled.value = false;
      }
    };

    onMounted(() => {
      updateReactions();
      userReactions.value = localStore.getItem('o-rcts');
      if (!userReactions.value) {
        userReactions.value = {};
        localStore.setItem('o-rcts', userReactions.value);
      }
      if (!userReactions.value[storageUserKey]) {
        userReactions.value[storageUserKey] = {};
        localStore.setItem('o-rcts', userReactions.value);
      }
    });

    return {
      handleReaction,
      showWarning,
      isReactionsDisabled,
      closeMenu,
      article,
      isMobile,
      handleClick,
      isMenuOpen,
      maxReaction,
      desktopReactionsRef,
      reactions,
    };
  },
});
</script>
